import { useEffect, useMemo, useRef, useState } from 'react';
import { useFitHeight } from '../lib/fitHeight';
import { api } from '../lib/api';
import { DataTable, ErrorBox, Pager, usePageSize, dash, money, moneyInput, useList, type Column, type Row, IdCell, MoneyInput } from '../components/DataTable';
import { RefPicker } from './MasterForm';
import { AllocPicker } from './FinInst';
import { canScreen, useMe } from '../lib/me';
import ResultDailyUploadPanel from './ResultDailyUpload';
import ResultDailyCardUploadPanel from './ResultDailyCardUpload';
import ResultDailyFuelSiteUpload from './ResultDailyFuelSiteUpload';
import DriveMonPicker from '../components/DriveMonPicker';
import { pickDriveMon, useStickyDriveMon } from '../lib/driveMon';
import Modal from '../components/Modal';
import { useAutoSearch } from '../lib/search';
import { useUrlSearch } from '../lib/deepLink';
import { useSort } from '../lib/sort';
import { isCorpId, useSticky } from '../lib/sticky';
import { AllocChangeBanner } from '../components/AllocChangeAlerts';
import { ROW_CHECKED_CLASS, useRowChecks } from '../lib/rowChecks';

/**
 * 재무추가 — 회계팀이 매일 쓰는 정산 원천 입력(레거시 `ResultDaily`).
 *
 * 이 표가 명세의 `Daily` 섹션이 된다. 여기 없으면 businweb 만으로는 정산을 못 돌린다
 * (원천 입력이 레거시에만 있었다 — .claude/issues.md 46).
 *
 * ⚠️ **마감된 달도 고칠 수 있다.** 레거시가 그렇고(SP 에 게이트가 없다) 다르게 만들 근거가 없다.
 *    대신 마감 상태를 화면에 띄운다 — 고치면 이미 만든 명세와 어긋나므로 명세를 다시 만들어야 한다.
 *
 * ⚠️ 계정명은 **마스터가 있다**(`result_daily_account` 103행/활성 66 — 2026-07-27 이관).
 *    처음엔 "마스터 없는 자유입력"으로 봤는데 틀렸다. 대량 업로드는 그걸로 검증하고
 *    **단건 입력만 검증을 안 해** 변종이 샜다(distinct 69종 중 17종이 마스터 밖).
 *    → 마스터를 먼저 제시하되 미등록값(⚠)도 **감추지 않는다**. 감추면 그 값을 쓰는 과거 행을 못 고친다.
 */

const EMPTY = '__carry__'; // 이월잔액(drive_mon = '')을 select 값으로 표현하기 위한 표식
const ALL = '__all__'; // 정산월 전체(#183) — 서버엔 '*'. 이월잔액 줄도 함께 나온다.

/** 「전체」에서 새 항목의 기본 정산월(#443) — 오늘 6일~다음 달 5일 = 그 달, 1~5일은 전월(출납일보 #57 과 같은 식). 창에서 바꿀 수 있다. */
function newItemMon(): string {
  const today = new Date(Date.now() + 9 * 3600_000).toISOString().slice(0, 10);
  if (Number(today.slice(8, 10)) > 5) return today.slice(0, 7);
  const y = Number(today.slice(0, 4));
  const m = Number(today.slice(5, 7));
  return m === 1 ? `${y - 1}-12` : `${y}-${String(m - 1).padStart(2, '0')}`;
}

/** 확인 체크(#142) — `lib/rowChecks.ts`(이 브라우저에만 기억 · 기사 4종 #382 과 공용). 키는 옛 값 그대로(이미 체크한 줄 유지). */
const CHECK_KEY = 'businweb.resultDaily.checked';

export default function ResultDaily() {
  const { me } = useMe();
  const canEdit = canScreen(me, 'result-daily');

  const [months, setMonths] = useState<string[]>([]);
  // 최신 정산월 뒤인데 재무추가에만 줄이 있는 달(#511 — 몇 달 뒤로 미리 넣은 공제). 선택기 맨 위에 따로.
  const [aheadMonths, setAheadMonths] = useState<string[]>([]);
  // 고른 정산년월은 화면을 옮겨도 남는다. `이월잔액`(EMPTY)은 월이 아니라 필터 모드라 기억되지 않는다.
  const [mon, setMon] = useStickyDriveMon();
  const [io, setIo] = useState<'' | 'plus' | 'minus'>('');
  // 법인 탭(#45) — 할부금과 같은 모양. 목록은 서버가 함께 내린다.
  // 고른 탭은 계정에 기억한다(#151 — 다른 메뉴에 갔다 와도 그 법인). 회계 화면 공용 `acct.corp` 와는 따로 —
  // 회계는 지점도 고를 수 있는데 여기 탭엔 지점이 없다.
  const [corpId, setCorpId] = useSticky<number | null>('result-daily.corp', null, { persist: true, valid: isCorpId });
  const [corps, setCorps] = useState<{ corp_id: number; short_name: string }[]>([]);
  const [term, setTerm] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [PAGE, setPAGE] = usePageSize('result-daily', 50);

  // 치는 대로 찾는다(입력이 멎으면 적용). 버튼·Enter 는 그대로 — `lib/search.ts` 참고.
  useAutoSearch(term, (v) => { setQ(v); setPage(0); });
  // 다른 화면에서 건너온 링크(?q=·?mon= — 정산현황 줄 보기 · lib/deepLink.ts)면 그 검색어로.
  useUrlSearch(setTerm, setQ, setMon);
  // 칸별 검색(#63) — 서버가 거른다(합계도 같은 조건). 치는 대로, 멎으면 적용(기준정보와 같은 방식).
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [filterDraft, setFilterDraft] = useState<Record<string, string>>({});
  useAutoSearch(JSON.stringify(filterDraft), () => { setFilters(filterDraft); setPage(0); });
  // 머리글 정렬(#89) — 서버가 한다(200줄씩 끊어 받는다 · `lib/sort.tsx`).
  const [sort, toggleSort] = useSort('result-daily');
  const [nonce, setNonce] = useState(0);
  const [editing, setEditing] = useState<Row | 'new' | null>(null);
  const [uploading, setUploading] = useState(false);
  const [cardUploading, setCardUploading] = useState(false);
  const [fuelUploading, setFuelUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<{ total: number; plus: string; minus: string; isFinished: boolean } | null>(null);
  // 차주 잔액(#261 · 구 버스인 왼쪽 「차주」 탭) — 열어 둔 상태는 계정에 기억. 차주를 고르면 그 차주 줄 전부(모든 달).
  const [remainOpen, setRemainOpen] = useSticky<boolean>('result-daily.remain-panel', false, {
    persist: true, valid: (v): v is boolean => typeof v === 'boolean',
  });
  const [owner, setOwner] = useState<{ owner_id: number; owner_name: string | null } | null>(null);

  useEffect(() => {
    api.listDriveMonths()
      .then((r) => {
        const ahead = r.ahead ?? [];
        setMonths(r.rows);
        setAheadMonths(ahead);
        // 기억된 달이 앞 달이어도 그대로 · 없으면 최신(r.rows[0]) — 앞 달은 뒤에 붙여 기본값이 미래로 안 밀리게.
        setMon(pickDriveMon([...r.rows, ...ahead], mon));
      })
      .catch(() => undefined);
    // 마운트 때 한 번만 — `mon` 은 기억된 값을 읽는 용도다(의존성에 넣으면 월마다 재조회).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { rows, loading } = useList(async (alive) => {
    const r = await api.listResultDaily({
      // 차주를 골랐으면 정산월과 무관하게 그 차주의 모든 달(구 버스인 「전체년월」).
      driveMon: owner ? '*' : mon === EMPTY ? '' : mon === ALL ? '*' : mon,
      ownerId: owner?.owner_id ?? null,
      io: io || undefined,
      corpId,
      q: q || undefined,
      filters,
      sort: sort?.key,
      dir: sort?.dir,
      limit: PAGE,
      offset: page * PAGE,
    });
    if (alive()) {
      if (r.corps) setCorps(r.corps);
      setSummary({ total: r.total, plus: r.plus, minus: r.minus, isFinished: r.isFinished });
    }
    return r as unknown as { rows: Row[] };
  }, [mon, io, corpId, q, filters, page, PAGE, nonce, sort, owner]);

  const { checked, toggle } = useRowChecks(CHECK_KEY);
  const toggleCheck = (r: Row) => toggle(String(r.result_daily_id));
  const carry = mon === EMPTY && !owner;
  const all = mon === ALL || owner !== null;

  async function remove(r: Row) {
    if (!window.confirm(`${dash(r.account_name)} ${money(r.account_amt)}원을 삭제할까요?`)) return;
    setError(null);
    try {
      await api.deleteResultDaily({ resultDailyId: Number(r.result_daily_id) });
      setNonce((n) => n + 1);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  const columns: Column[] = [
    {
      key: '_check',
      label: '확인',
      noSort: true,
      render: (r) => (
        <input
          type="checkbox"
          className="h-4 w-4 accent-emerald-600"
          checked={checked.has(String(r.result_daily_id))}
          onClick={(e) => e.stopPropagation()}
          onChange={() => toggleCheck(r)}
        />
      ),
    },
    // NO = 레거시 ResultDailyId 와 같은 번호. 이 번호로 소통하고, 검색창에 숫자만 치면 찾는다.
    // phone: 폰에선 기사를 왼쪽에 고정하고 번호·법인·ID·입력 방식 칸은 뺀다(DataTable `phone` · 2026-09-30 way).
    { key: 'result_daily_id', label: 'NO', align: 'right', phone: 'hide', render: (r) => <span className="text-xs text-ink-soft">{dash(r.result_daily_id)}</span> },
    {
      key: 'is_plus',
      label: '구분',
      render: (r) =>
        r.is_plus === true
          ? <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-xs font-medium text-emerald-700">지급</span>
          : <span className="rounded bg-red-50 px-1.5 py-0.5 text-xs font-medium text-red-700">공제</span>,
    },
    // 정산월 전체(#183)면 줄마다 어느 달인지.
    ...(all ? [{ key: 'drive_mon', label: '정산월', render: (r: Row) => (r.drive_mon === '' ? '이월잔액' : dash(r.drive_mon)) } as Column] : []),
    { key: 'corp_name', label: '법인', phone: 'hide' },
    { key: 'account_name', label: '계정' },
    { key: 'account_amt', label: '금액', align: 'right', render: (r) => <b>{money(r.account_amt)}</b> },
    { key: 'car_alloc_id', label: '편성 ID', align: 'right', phone: 'hide', render: (r) => <IdCell v={r.car_alloc_id} warn /> },
    { key: 'driver_id', label: '기사 ID', align: 'right', phone: 'hide', render: (r) => <IdCell v={r.driver_id} /> },
    { key: 'owner_id', label: '차주 ID', align: 'right', phone: 'hide', render: (r) => <IdCell v={r.owner_id} /> },
    { key: 'owner_name', label: '차주' },
    { key: 'driver_name', label: '기사', phone: 'pin' },
    { key: 'car_no', label: '차량' },
    // 이월잔액(#143) — 그 차량·차주의 이월잔액 줄 전부의 지급 − 공제(레거시 차량·차주 잔액).
    ...(carry
      ? ([
          // 이름(함남열 10-08): 버스인 「차주잔액」 = 그 차량 기준 → 「차주 잔액(이 차량)」 · 차주의 모든 차량 합은 따로 「(전 차량)」.
          { key: 'car_remain', label: '차주 잔액(이 차량)', align: 'right', noSort: true, render: (r) => <Remain v={r.car_remain} /> },
          { key: 'owner_remain', label: '차주 잔액(전 차량)', align: 'right', noSort: true, render: (r) => <Remain v={r.owner_remain} /> },
        ] as Column[])
      : []),
    // 차주를 고르면(#261) 이월잔액 줄마다 입력순 누계 — 구 버스인 「차주잔액합계」. 마지막 줄 = 지금 잔액.
    ...(owner
      ? [{ key: 'running_remain', label: '차주 잔액 누계', align: 'right', noSort: true, render: (r: Row) => <Remain v={r.running_remain} /> } as Column]
      : []),
    { key: 'ex_date', label: '발생일' },
    { key: 'comment', label: '비고' },
    {
      key: 'create_method',
      label: '입력',
      phone: 'hide',
      render: (r) => <span className="text-xs text-ink-faint">{dash(r.create_method)}</span>,
    },
    // 버스인입력담당자(#461 정여진 → 11-03 way 이름) — 서버가 이메일·레거시 로그인 ID 를 사람 이름으로 풀어 준다
    // (`created_name` · 일괄 스크립트 줄은 「자동」). 마우스를 올리면 원래 ID.
    {
      key: 'created_by',
      label: '버스인입력담당자',
      phone: 'hide',
      render: (r) => (
        <span className="text-xs text-ink-faint" title={r.created_by ? String(r.created_by) : undefined}>
          {dash(r.created_name ?? (r.created_by ? String(r.created_by).split('@')[0] : null))}
        </span>
      ),
    },
    ...(canEdit
      ? [{
          key: '_del',
          label: '',
          noSort: true,
          render: (r: Row) => (
            <button
              className="btn-ghost py-0.5 text-xs text-red-600"
              onClick={(e) => { e.stopPropagation(); void remove(r); }}
            >
              삭제
            </button>
          ),
        } as Column]
      : []),
  ];

  return (
    <div className="space-y-4">
      {/* 운행편성 변경(10-02) — 이 정산월에 편성이 바뀐 차량이 있으면 늘 보인다 */}
      <AllocChangeBanner mon={mon} />
      <div className="page-head">
        <h1 className="font-bold">재무추가</h1>
        <p className="mt-1 text-sm text-ink-soft">
          기사·차주별 추가 지급과 공제를 입력합니다. 여기 넣은 항목이 그 달 <b>급여명세</b>에 그대로 들어갑니다.
        </p>
      </div>

      <div className="btn-row-sm flex flex-wrap items-center gap-2">
        <DriveMonPicker
          months={months}
          ahead={aheadMonths}
          value={mon}
          extra={{ value: EMPTY, label: '이월잔액' }}
          extraAt="last"
          head={{ value: ALL, label: '전체' }}
          onChange={(m) => { setMon(m); setOwner(null); setPage(0); setEditing(null); }}
        />

        <div className="flex gap-1">
          {([[null, '전체 법인'], ...corps.map((c) => [c.corp_id, c.short_name] as const)] as const).map(([id, label]) => (
            <button key={String(id)} onClick={() => { setCorpId(id); setPage(0); }}
                    className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
                      corpId === id ? 'bg-brand-600 font-semibold text-white shadow-sm' : 'text-ink hover:bg-brand-50'}`}>
              {label}
            </button>
          ))}
        </div>
        <span className="h-5 w-px bg-line" aria-hidden />

        <div className="flex gap-1">
          {([['', '전체'], ['plus', '지급'], ['minus', '공제']] as const).map(([id, label]) => (
            <button
              key={id}
              onClick={() => { setIo(id); setPage(0); }}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
                io === id ? 'bg-brand-600 font-semibold text-white shadow-sm' : 'text-ink hover:bg-brand-50'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); setQ(term.trim()); setPage(0); }}
          className="flex items-center gap-2"
        >
          <input
            className="field w-56"
            placeholder="계정·기사·차주·차량번호·NO"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
          />
          <button className="btn-search" type="submit" aria-label="검색" title="검색" />
        </form>

        <button
          className={remainOpen ? 'btn-primary' : 'btn-ghost'}
          onClick={() => { if (remainOpen) setOwner(null); setRemainOpen(!remainOpen); setPage(0); }}
        >
          차주 잔액
        </button>

        {canEdit && (
          <div className="ml-auto flex gap-2">
            <button className="btn-ghost" onClick={() => { setUploading((v) => !v); setCardUploading(false); setFuelUploading(false); setEditing(null); }}>
              대량 등록
            </button>
            {/* 카드는 매칭 키가 달라(카드번호) 별도 패널이다 — 한 화면에 섞으면 열 구성이 매번 바뀐다. */}
            <button className="btn-ghost" onClick={() => { setCardUploading((v) => !v); setUploading(false); setFuelUploading(false); setEditing(null); }}>
              카드 대량등록
            </button>
            {/* 유가보조금 사이트 카드는 편성 카드와 다른 카드라 차량번호로 붙인다(issues-settle-input §72). */}
            <button className="btn-ghost" onClick={() => { setFuelUploading((v) => !v); setUploading(false); setCardUploading(false); setEditing(null); }}>
              유가보조금 자료
            </button>
            <button className="btn" disabled={owner !== null}
                    title={owner ? '차주 보기를 풀고 넣을 수 있습니다' : all ? '정산월은 창에서 고릅니다' : undefined}
                    onClick={() => { setEditing('new'); setUploading(false); setCardUploading(false); setFuelUploading(false); }}>
              새 항목
            </button>
          </div>
        )}
      </div>

      {summary && (
        <div className="flex flex-wrap items-center gap-4 rounded-lg border border-line bg-surface px-3 py-2 text-sm">
          <span className="text-ink-soft">{summary.total.toLocaleString('ko-KR')}건</span>
          <span>지급 <b className="text-emerald-700">{money(summary.plus)}</b></span>
          <span>공제 <b className="text-red-700">{money(summary.minus)}</b></span>
          {summary.isFinished && (
            <span className="rounded bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800">
              이 달은 기사정산 마감됨 — 고치면 이미 만든 명세와 어긋납니다(명세를 다시 만들어야 합니다)
            </span>
          )}
          {/* 차주 보기 안내는 이 줄 안에(#280 정여진 — 따로 한 줄을 띄우면 표가 그만큼 밀려 내려갔다). */}
          {owner && (
            <span className="ml-auto flex items-center gap-2 rounded bg-brand-50 px-2 py-0.5 text-xs text-brand-800"
                  title="정산월 없는 줄 오른쪽 「차주 잔액 누계」의 마지막 값이 지금 잔액입니다.">
              차주 <b>{owner.owner_name ?? owner.owner_id}</b> 모든 달 · 입력순
              <button className="underline" onClick={() => { setOwner(null); setPage(0); }}>차주 보기 풀기</button>
            </span>
          )}
        </div>
      )}

      {error && <ErrorBox message={error} />}


      {uploading && mon !== EMPTY && !all && (
        <ResultDailyUploadPanel driveMon={mon} onDone={() => { setUploading(false); setNonce((n) => n + 1); }} />
      )}

      {cardUploading && mon !== EMPTY && !all && (
        <ResultDailyCardUploadPanel driveMon={mon} onDone={() => { setCardUploading(false); setNonce((n) => n + 1); }} />
      )}

      {/* 등록 뒤에도 패널을 닫지 않는다 — 뺀 차량 목록을 보고 따로 넣어야 한다. */}
      {fuelUploading && mon !== EMPTY && !all && (
        <ResultDailyFuelSiteUpload driveMon={mon} onDone={() => setNonce((n) => n + 1)} />
      )}

      {editing && (
        <Modal>
          <ResultDailyForm
            // 전체(#183)에서 연 줄은 그 줄의 정산월로 고친다.
            driveMon={all ? (editing === 'new' ? newItemMon() : String(editing.drive_mon ?? '')) : mon === EMPTY ? '' : mon}
            row={editing === 'new' ? null : editing}
            onClose={() => setEditing(null)}
            onSaved={(keepOpen) => { if (!keepOpen) setEditing(null); setNonce((n) => n + 1); }}
          />
        </Modal>
      )}

      <div className="flex items-start gap-3">
      {remainOpen && (
        <OwnerRemainPanel
          selected={owner?.owner_id ?? null}
          nonce={nonce}
          onPick={(o) => { setOwner(o); setPage(0); setEditing(null); }}
        />
      )}
      <div className="min-w-0 flex-1 space-y-2">
      <DataTable
        tableId="settle.resultDaily"
        columns={columns}
        rows={rows}
        loading={loading}
        onRowClick={canEdit ? (r) => setEditing(r) : undefined}
        activeRow={(r) => editing !== 'new' && editing === r}
        rowClass={(r) => (checked.has(String(r.result_daily_id)) ? ROW_CHECKED_CLASS : '')}
        filters={filterDraft}
        onFilter={(key, value) => setFilterDraft((cur) => ({ ...cur, [key]: value }))}
        sort={sort}
        onSort={(k) => { toggleSort(k); setPage(0); }}
      />
      <Pager page={page} setPage={setPage} rowCount={rows.length} pageSize={PAGE} loading={loading} setPageSize={setPAGE} baseSize={50} />
      </div>
      </div>
    </div>
  );
}

/**
 * 차주 잔액 목록(#261 · 구 버스인 재무추가 왼쪽 「차주」 탭) — 차주별 이월잔액(정산월 없는 줄의 지급 − 공제).
 * 기본은 재무추가 줄이 있는 차주 전부(잔액 0 포함 · #261 후속), 이름·기사·차량번호를 치면 그 차주만. 누르면 오른쪽 표가 그 차주 줄로 바뀐다(다시 누르면 풀림).
 * 목록 높이는 화면에 맞춘다(#280 — 길면 페이지째 내려가 한 화면에 안 들어왔다).
 */
function OwnerRemainPanel({ selected, nonce, onPick }: {
  selected: number | null;
  nonce: number;
  onPick: (o: { owner_id: number; owner_name: string | null } | null) => void;
}) {
  const [term, setTerm] = useState('');
  const [q, setQ] = useState('');
  useAutoSearch(term, setQ);
  const [rows, setRows] = useState<{ owner_id: number; owner_name: string | null; amt: string | null; n: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let off = false;
    setLoading(true);
    api.listOwnerRemain({ q: q || undefined })
      .then((r) => { if (!off) { setRows(r.rows); setErr(null); } })
      .catch((e) => { if (!off) setErr(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (!off) setLoading(false); });
    return () => { off = true; };
  }, [q, nonce]);
  const sum = rows.reduce((s, r) => s + Number(r.amt), 0);
  // #302 머리글 누르면 오름 → 내림 → 처음(서버 순서). 이 브라우저에 기억.
  const [sort, setSort] = useState<{ key: 'name' | 'amt'; dir: 1 | -1 } | null>(() => {
    try { return JSON.parse(localStorage.getItem('result-daily.remain-sort') || 'null'); } catch { return null; }
  });
  function toggleSort(key: 'name' | 'amt') {
    const next = !sort || sort.key !== key ? { key, dir: 1 as const } : sort.dir === 1 ? { key, dir: -1 as const } : null;
    setSort(next);
    try { localStorage.setItem('result-daily.remain-sort', JSON.stringify(next)); } catch { /* 저장 안 돼도 그대로 */ }
  }
  const shown = useMemo(() => {
    if (!sort) return rows;
    const cmp = sort.key === 'amt'
      ? (a: typeof rows[number], b: typeof rows[number]) => Number(a.amt) - Number(b.amt)
      : (a: typeof rows[number], b: typeof rows[number]) => String(a.owner_name ?? '').localeCompare(String(b.owner_name ?? ''), 'ko');
    // 잔액 「—」(명부에서 지운 차주 · #345)는 어느 방향이든 맨 뒤.
    const blank = (r: typeof rows[number]) => (sort.key === 'amt' && r.amt == null ? 1 : 0);
    return [...rows].sort((a, b) => blank(a) - blank(b) || cmp(a, b) * sort.dir || a.owner_id - b.owner_id);
  }, [rows, sort]);
  const arrow = (key: 'name' | 'amt') => (sort?.key === key ? (sort.dir === 1 ? ' ▲' : ' ▼') : ' ↕');
  const boxRef = useRef<HTMLDivElement>(null);
  const maxHeight = useFitHeight(boxRef, [rows.length, loading], { min: '12rem' });

  return (
    <div className="card w-64 shrink-0 space-y-2 p-3">
      <div className="text-sm font-semibold">차주 잔액</div>
      <input className="field w-full" placeholder="차주·기사 이름·차량번호" value={term} onChange={(e) => setTerm(e.target.value)} />
      <p className="text-xs text-ink-faint">
        {q ? '이름·기사·차량번호가 맞는 차주' : '재무추가가 있는 차주 전부'} · 잔액 = 정산월 없이 넣은 재무추가의 지급 − 공제 · 차주 명부에서 지운 분은 잔액 「—」
      </p>
      {err && <ErrorBox message={err} />}
      <div ref={boxRef} className="overflow-auto rounded border border-line" style={{ maxHeight }}>
        <table className="w-full text-sm tabular-nums">
          <thead>
            <tr className="text-xs text-ink-faint">
              <th className="sticky top-0 cursor-pointer select-none bg-surface px-2 py-1 text-left font-medium hover:text-ink" onClick={() => toggleSort('name')} title="누르면 이름 오름/내림차순">차주{arrow('name')}</th>
              <th className="sticky top-0 cursor-pointer select-none bg-surface px-2 py-1 text-right font-medium hover:text-ink" onClick={() => toggleSort('amt')} title="누르면 잔액 오름/내림차순">잔액{arrow('amt')}</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr
                key={r.owner_id}
                className={`cursor-pointer border-t border-line ${selected === r.owner_id ? 'bg-brand-50 font-semibold' : 'hover:bg-canvas'}`}
                onClick={() => onPick(selected === r.owner_id ? null : { owner_id: r.owner_id, owner_name: r.owner_name })}
              >
                <td className="px-2 py-1">{dash(r.owner_name)}</td>
                <td className="px-2 py-1 text-right"><Remain v={r.amt} /></td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={2} className="px-2 py-4 text-center text-xs text-ink-faint">없음</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="flex justify-between text-sm tabular-nums">
        <span className="text-ink-soft">{loading ? '불러오는 중…' : `${rows.length}명`}</span>
        <b className={sum < 0 ? 'text-red-700' : ''}>{money(sum)}</b>
      </div>
    </div>
  );
}

/** 잔액 칸 — 음수는 빨강. 차량·차주가 비어 있으면 대시. */
function Remain({ v }: { v: unknown }) {
  if (v === null || v === undefined || v === '') return <span className="text-xs text-ink-faint">—</span>;
  return <span className={Number(v) < 0 ? 'text-red-700' : ''}>{money(v)}</span>;
}

/** 등록·수정 폼. 계정명은 실사용 값 자동완성(datalist 는 안 쓴다 — 고른 값이 목록을 가린다). */
function ResultDailyForm({
  driveMon, row, onClose, onSaved,
}: {
  driveMon: string;
  row: Row | null;
  onClose: () => void;
  /** keepOpen = 「저장 후 계속 등록」(#144) — 창을 닫지 않고 목록만 다시 읽는다. */
  onSaved: (keepOpen?: boolean) => void;
}) {
  // 정산월은 창에서 바꾸거나 지울 수 있다(#270 새 등록 — 지우면 정산월 없이 = 이월잔액 · #316 수정도 — 지우고 다시 넣지 않게).
  const rowMon = String(row?.drive_mon ?? '');
  // 옛 이관 값(2000-01-01 등 YYYY-MM 아님)은 고르는 칸에 안 들어가 목록의 달로 둔다(예전 동작).
  const [mon, setMon] = useState(row ? (rowMon === '' || /^[0-9]{4}-[0-9]{2}$/.test(rowMon) ? rowMon : driveMon) : driveMon);
  const [accountName, setAccountName] = useState(String(row?.account_name ?? ''));
  const [isPlus, setIsPlus] = useState(row ? row.is_plus === true : false);
  const [amt, setAmt] = useState(moneyInput(row?.account_amt));
  const [exDate, setExDate] = useState(String(row?.ex_date ?? ''));
  const [comment, setComment] = useState(String(row?.comment ?? ''));
  const [ownerId, setOwnerId] = useState<number | null>(row?.owner_id ? Number(row.owner_id) : null);
  const [driverId, setDriverId] = useState<number | null>(row?.driver_id ? Number(row.driver_id) : null);
  const [carId, setCarId] = useState<number | null>(row?.car_id ? Number(row.car_id) : null);
  const [grpId, setGrpId] = useState<number | null>(row?.grp_id ? Number(row.grp_id) : null);
  // 편성 검색으로 고르면 네 칸을 한 번에 채운다(#103 — 출납일보처럼). 칸을 따로 바꾸면 편성 연결은 푼다.
  const [carAllocId, setCarAllocId] = useState<number | null>(row?.car_alloc_id ? Number(row.car_alloc_id) : null);
  const [labels, setLabels] = useState({
    owner: String(row?.owner_name ?? ''), driver: String(row?.driver_name ?? ''),
    car: String(row?.car_no ?? ''), grp: String(row?.grp_name ?? ''),
  });
  const [pickKey, setPickKey] = useState(0); // 칸 이름표를 새로 그리게(RefPicker 는 처음 받은 이름표만 쓴다)
  const num = (v: unknown) => (v == null || v === '' ? null : Number(v));

  const [hints, setHints] = useState<{ account_name: string; used_cnt: number; is_plus: boolean; in_master: boolean }[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 계정 = 등록된 계정(마스터)만 고른다(10-12 way). 한 번에 다 받아 구분(공제/지급)으로 거른다 — 실사용 많은 순.
  useEffect(() => {
    api.listAccountNames({ limit: 300 })
      .then((r) => setHints(r.rows as never))
      .catch(() => undefined);
  }, []);
  const origName = String(row?.account_name ?? '');
  const accountOptions = hints.filter((h) => h.in_master && h.is_plus === isPlus);
  // 고치는 줄의 옛 계정이 마스터 밖(⚠)이면 그 값만 그대로 둘 수 있게 보여 준다(서버도 「안 바꾼 옛 값」만 허용).
  const legacyName = row && origName && (row.is_plus === true) === isPlus && !accountOptions.some((h) => h.account_name === origName) ? origName : null;

  // 저장 후 계속 등록(#144) — 레거시 「고정: 차량정보 / 정산정보」. 고정한 쪽은 남기고 나머지를 비운다.
  const [keep, setKeep] = useState<'car' | 'settle'>('car');
  const [saved, setSaved] = useState<string | null>(null);

  async function submit(e: React.FormEvent, cont = false) {
    e.preventDefault();
    setSaved(null);
    setBusy(true);
    setError(null);
    try {
      await api.saveResultDaily({
        resultDailyId: row ? Number(row.result_daily_id) : undefined,
        driveMon: mon,
        accountName,
        isPlus,
        accountAmt: amt,          // 문자열 그대로 보낸다 — Number 로 바꾸면 소수가 흔들린다
        exDate: exDate || undefined,
        comment: comment || undefined,
        ownerId: ownerId ?? undefined,
        driverId: driverId ?? undefined,
        carId: carId ?? undefined,
        grpId: grpId ?? undefined,
        carAllocId: carAllocId ?? undefined,
      });
      if (cont) {
        setSaved(`${mon || '이월잔액'} · ${accountName} ${money(amt)}원 저장됨`);
        // 구분·계정명·발생일은 어느 쪽을 고정해도 직전 값을 남긴다(#158 — 같은 계정을 여러 대에 이어 넣는다).
        setAmt(''); setComment('');
        if (keep === 'settle') {
          setOwnerId(null); setDriverId(null); setCarId(null); setGrpId(null); setCarAllocId(null);
          setLabels({ owner: '', driver: '', car: '', grp: '' }); setPickKey((k) => k + 1);
        }
        onSaved(true);
      } else onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="card space-y-3 p-4">
      <div className="flex items-center gap-3">
        <h2 className="text-sm font-bold">
          {row ? '재무추가 수정' : '재무추가 등록'}
        </h2>
        {row && mon !== rowMon && (
          <span className="text-xs font-semibold text-brand-600">정산월을 {rowMon || '이월잔액'} → {mon || '이월잔액'} 으로 옮깁니다</span>
        )}
        {(
          <span className="flex items-center gap-1 text-sm">
            <span className="text-xs text-ink-soft">정산월</span>
            <input
              type="month"
              className="field py-1"
              value={mon}
              onChange={(e) => setMon(e.target.value)}
              aria-label="정산월"
            />
            {mon !== '' ? (
              <button type="button" className="btn-ghost py-1 text-xs" onClick={() => setMon('')}>지우기</button>
            ) : (
              <span className="text-xs font-semibold text-amber-700">정산월 없음(이월잔액)</span>
            )}
          </span>
        )}
        <button type="button" className="btn-ghost ml-auto py-1 text-xs" onClick={onClose}>닫기</button>
      </div>

      {error && <ErrorBox message={error} />}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <label className="space-y-1">
          <span className="text-xs text-ink-soft">구분</span>
          <select className="field w-full" value={isPlus ? 'plus' : 'minus'} onChange={(e) => setIsPlus(e.target.value === 'plus')}>
            <option value="minus">공제</option>
            <option value="plus">지급</option>
          </select>
        </label>

        <label className="space-y-1 sm:col-span-2">
          <span className="text-xs text-ink-soft">계정명</span>
          <select className="field w-full" value={accountName} onChange={(e) => setAccountName(e.target.value)} required>
            <option value="">계정을 고르세요</option>
            {legacyName && <option value={legacyName}>{legacyName} (⚠ 등록 안 된 옛 계정)</option>}
            {accountOptions.map((h) => <option key={h.account_name} value={h.account_name}>{h.account_name}</option>)}
            {accountName && accountName !== legacyName && !accountOptions.some((h) => h.account_name === accountName) && (
              <option value={accountName}>{accountName} (⚠ {isPlus ? '지급' : '공제'} 계정에 없음)</option>
            )}
          </select>
        </label>

        <label className="space-y-1">
          <span className="text-xs text-ink-soft">금액</span>
          <MoneyInput className="w-full" value={amt} onChange={setAmt} required placeholder="0" />
        </label>
      </div>

      <AllocPicker
        value={carAllocId}
        onPick={setCarAllocId}
        includeFuture={mon === ''}
        startedBy={mon ? `${mon}-31` : null}
        onPickRow={(r) => {
          setOwnerId(num(r.owner_id)); setDriverId(num(r.driver_id));
          setCarId(num(r.car_id)); setGrpId(num(r.grp_id));
          setLabels({ owner: r.owner_name ?? '', driver: r.driver_name ?? '', car: r.car_no ?? '', grp: r.grp_name ?? '' });
          setPickKey((k) => k + 1);
        }}
      />
      <div key={pickKey} className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <RefPicker kind="owner" value={ownerId} label={labels.owner} onPick={(id) => { setOwnerId(id); setCarAllocId(null); }} />
        <RefPicker kind="driver" value={driverId} label={labels.driver} onPick={(id) => { setDriverId(id); setCarAllocId(null); }} />
        <RefPicker kind="car" value={carId} label={labels.car} onPick={(id) => { setCarId(id); setCarAllocId(null); }} />
        <RefPicker kind="grp" value={grpId} label={labels.grp} onPick={setGrpId} />
      </div>
      <p className="text-xs text-ink-faint">
        편성에서 기사·차량번호로 찾아 고르면 차주·기사·차량·소속이 한 번에 채워집니다. 차주 또는 기사 중 하나는 있어야 합니다.
      </p>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="space-y-1">
          <span className="text-xs text-ink-soft">발생일</span>
          {/* 달력(#158) — 레거시 이관분엔 날짜 꼴이 아닌 글자가 있어 그런 줄은 글자 칸 그대로(date 칸에 넣으면 저장 때 지워진다 · issues 106) */}
          {!exDate || /^\d{4}-\d{2}-\d{2}$/.test(exDate)
            ? <input type="date" className="field w-full" value={exDate} onChange={(e) => setExDate(e.target.value)} />
            : <input className="field w-full" value={exDate} onChange={(e) => setExDate(e.target.value)} />}
        </label>
        <label className="space-y-1">
          <span className="text-xs text-ink-soft">비고</span>
          <input className="field w-full" value={comment} onChange={(e) => setComment(e.target.value)} />
        </label>
      </div>

      <div className="flex flex-wrap items-center justify-end gap-3">
        {saved && <span className="mr-auto text-xs text-emerald-700">✓ {saved}</span>}
        {!row && (
          <>
            <span className="flex items-center gap-2 text-xs text-ink-soft">
              고정
              <label className="flex items-center gap-1"><input type="radio" checked={keep === 'car'} onChange={() => setKeep('car')} />차량정보</label>
              <label className="flex items-center gap-1"><input type="radio" checked={keep === 'settle'} onChange={() => setKeep('settle')} />정산정보</label>
            </span>
            <button type="button" className="btn-ghost" disabled={busy} onClick={(e) => void submit(e, true)}>저장 후 계속 등록</button>
          </>
        )}
        <button className="btn" disabled={busy}>{busy ? '저장 중…' : row ? '수정' : '등록'}</button>
      </div>
    </form>
  );
}
